import {
    Children,
    isValidElement,
    MouseEvent,
    ReactElement,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { cn } from "../../../lib/utils";

type TabProps = {
    label: string;
    children?: ReactNode;
};

type TabElement = ReactElement<TabProps>;

type TabsProps = {
    startActivePos?: number
    children: ReactNode;
    className?: string;
    onClick?: (newActiveTab: string) => void;
};

const isTabElement = (child: ReactNode): child is TabElement =>
    isValidElement<TabProps>(child) && typeof child.props.label === "string";

const Tabs = ({ startActivePos, children, className, onClick: callback }: TabsProps) => {
    const tabElements = useMemo<TabElement[]>(
        () => Children.toArray(children).filter(isTabElement),
        [children],
    );

    const [activeTab, setActiveTab] = useState<string>(() => tabElements[(startActivePos && startActivePos <= tabElements.length && startActivePos) || 0]?.props.label ?? "");

    useEffect(() => {
        if (!tabElements.length) {
            if (activeTab !== "") {
                setActiveTab("");
            }
            return;
        }

        if (!tabElements.some((tab) => tab.props.label === activeTab)) {
            setActiveTab(tabElements[0].props.label);
        }
    }, [activeTab, tabElements]);

    const handleClick = (e: MouseEvent<HTMLButtonElement>, newActiveTab: string) => {
        e.preventDefault();
        setActiveTab(newActiveTab);
        if (callback) {
            callback(newActiveTab);
        }
    };

    return (
        <div className="max-w-full mx-auto">
            <div
                className={cn(
                    "flex border border-gray-200 rounded-xl transition-all duration-300 -mb-px overflow-hidden",
                    className,
                )}
            >
                {tabElements.map((child) => (
                    <button
                        key={child.props.label}
                        className={`${activeTab === child.props.label ? "border-b-2 border-plt-secondary" : ""} flex-1 font-bold py-2`}
                        onClick={(e) => handleClick(e, child.props.label)}
                    >
                        {child.props.label}
                    </button>
                ))}
            </div>
            <div className="">
                {tabElements.map((child) => {
                    if (child.props.label === activeTab) {
                        return <div key={child.props.label}>{child.props.children}</div>;
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

const Tab = ({ label, children }: TabProps) => {
    return (
        <div className="hidden" data-label={label}>
            {children}
        </div>
    );
};
export { Tabs, Tab };
